import { CheckCircle2, RefreshCw, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { fetchJson, sendJson } from "../api/rest";
import {
	btn,
	ErrorBanner,
	input,
	Panel,
	Spinner,
} from "../components/flows/ui";
import { PageHeader } from "../components/layout/PageHeader";
import type {
	CacheCatalogue,
	CacheItemResult,
	CacheRevalidateResponse,
} from "../types/cache";

const ResultRow = ({ item }: { item: CacheItemResult }) => {
	const [open, setOpen] = useState(false);
	return (
		<>
			<tr className="border-b border-slate-100 align-top text-slate-700">
				<td className="py-1.5 pr-3">
					{item.ok ? (
						<CheckCircle2 className="h-4 w-4 text-emerald-600" />
					) : (
						<XCircle className="h-4 w-4 text-danger" />
					)}
				</td>
				<td className="whitespace-nowrap py-1.5 pr-4 text-slate-500">
					{item.kind}
				</td>
				<td className="py-1.5 pr-4 font-mono text-[11px]">{item.input}</td>
				<td className="py-1.5 pr-4 font-mono text-[11px] text-slate-500">
					{item.note ?? ""}
				</td>
				<td className="py-1.5 pr-4">
					{item.error ? (
						<span className="text-danger">{item.error}</span>
					) : (
						<button
							type="button"
							className="text-[11px] text-primary hover:underline"
							onClick={() => setOpen((v) => !v)}
						>
							{item.paths.length} paths
							{item.tags.length > 0 ? `, tags ${item.tags.join(", ")}` : ""}
						</button>
					)}
				</td>
			</tr>
			{open && (
				<tr className="border-b border-slate-100">
					<td />
					<td colSpan={4} className="pb-2 font-mono text-[11px] text-slate-500">
						{item.paths.map((path) => (
							<div key={path}>{path}</div>
						))}
					</td>
				</tr>
			)}
		</>
	);
};

export default function CachePage() {
	const [catalogue, setCatalogue] = useState<CacheCatalogue | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [presets, setPresets] = useState<Set<string>>(new Set());
	const [inputs, setInputs] = useState<Record<string, string>>({});
	const [running, setRunning] = useState(false);
	const [result, setResult] = useState<CacheRevalidateResponse | null>(null);

	const load = useCallback(async () => {
		try {
			setCatalogue(await fetchJson<CacheCatalogue>("/cache"));
		} catch (e) {
			setError(String(e));
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const items = Object.entries(inputs)
		.filter(([, value]) => value.trim())
		.map(([kind, value]) => ({ kind, input: value }));
	const nothingChosen = presets.size === 0 && items.length === 0;

	const togglePreset = (key: string) =>
		setPresets((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});

	const revalidate = async () => {
		setRunning(true);
		setError(null);
		setResult(null);
		try {
			const response = await sendJson<CacheRevalidateResponse>(
				"/cache/revalidate",
				"POST",
				{ presets: [...presets], items },
			);
			setResult(response);
			if (response.error) setError(response.error);
		} catch (e) {
			setError(String(e));
		} finally {
			setRunning(false);
		}
	};

	const clear = () => {
		setPresets(new Set());
		setInputs({});
		setResult(null);
		setError(null);
	};

	const failed = result?.items.filter((item) => !item.ok).length ?? 0;

	return (
		<div className="space-y-6">
			<PageHeader
				kicker="Monitoring · Cache"
				title="Cache."
				qualifier="Show new content now."
				description="Make the storefront show new content now instead of when its cache expires (up to four hours). Cloudflare can keep a page for up to an hour on top of that, so purge the URL there too when it has to change immediately."
				actions={
					<>
						<button type="button" className={btn.ghost} onClick={clear}>
							Clear
						</button>
						<button
							type="button"
							className={btn.primary}
							disabled={running || nothingChosen}
							onClick={revalidate}
						>
							<RefreshCw
								className={`h-4 w-4 ${running ? "animate-spin" : ""}`}
							/>
							{running ? "Revalidating…" : "Revalidate"}
						</button>
					</>
				}
			/>

			<ErrorBanner error={error} />
			{!catalogue && !error && <Spinner />}

			{catalogue && (
				<>
					<Panel title="Whole sections">
						<div className="grid gap-3 md:grid-cols-2">
							{catalogue.presets.map((preset) => (
								<label
									key={preset.key}
									className="flex cursor-pointer items-start gap-3 rounded-md border border-hairline p-3 hover:bg-canvas"
								>
									<input
										type="checkbox"
										className="mt-0.5"
										checked={presets.has(preset.key)}
										onChange={() => togglePreset(preset.key)}
									/>
									<span>
										<span className="block text-[13px] font-medium text-slate-700">
											{preset.label}
										</span>
										<span className="block text-[12px] text-slate-500">
											{preset.description}
										</span>
									</span>
								</label>
							))}
						</div>
					</Panel>

					<Panel
						title="Specific pages"
						hint="One per line, or separated by commas. A pasted URL works in any box."
					>
						<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
							{catalogue.kinds.map((kind) => (
								<label key={kind.key} className="block">
									<span className="block text-[13px] font-medium text-slate-700">
										{kind.label}
									</span>
									<span className="mb-1 block text-[11px] text-slate-400">
										{kind.accepts}
									</span>
									<textarea
										className={`${input} h-24 font-mono text-[12px]`}
										placeholder={kind.placeholder}
										value={inputs[kind.key] ?? ""}
										onChange={(e) =>
											setInputs((prev) => ({
												...prev,
												[kind.key]: e.target.value,
											}))
										}
									/>
								</label>
							))}
						</div>
					</Panel>
				</>
			)}

			{result && (
				<Panel
					title="Result"
					hint={
						failed > 0
							? `${failed} of ${result.items.length} failed`
							: `${result.items.length} revalidated`
					}
				>
					<div className="overflow-x-auto">
						<table className="w-full text-left text-[12px]">
							<thead>
								<tr className="border-b border-slate-200 text-slate-500">
									<th className="py-1.5 pr-3 font-medium" />
									<th className="py-1.5 pr-4 font-medium">Kind</th>
									<th className="py-1.5 pr-4 font-medium">Input</th>
									<th className="py-1.5 pr-4 font-medium">Resolved</th>
									<th className="py-1.5 pr-4 font-medium">Revalidated</th>
								</tr>
							</thead>
							<tbody>
								{result.items.map((item, index) => (
									<ResultRow
										key={`${item.kind}-${item.input}-${index}`}
										item={item}
									/>
								))}
							</tbody>
						</table>
					</div>
				</Panel>
			)}
		</div>
	);
}
